import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  IconButton,
  List,
  ListItemText,
  Skeleton,
  Stack,
} from '@mui/material';
import ListItem from '@mui/material/ListItem';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { dateFormatter, enum2NumberArray, isNumeric } from 'core/utils/commonUtils';
import { getOrderActionName } from 'core/utils/orderUtils';
import useOrderActions, { SWR_ORDER_ACTIONS_ENDPOINT } from 'hooks/swr/useOrderActions';
import useResponsive from 'hooks/useResponsive';
import { cloneDeep, isArray, isNil, sortBy, toNumber, uniq } from 'lodash';
import { OrderActionEnum } from 'models/enums/orderActionEnum';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as actionsService from 'services/orderActionService';
import { mutate } from 'swr';
import ActionEditDialog from './ActionEditDialog';
import type { TypographyProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { OrderActionInfo } from 'models/order/orderActionInfo';
import type { FC } from 'react';
import type { CreateOrUpdateOrderActionRequest } from 'services/dtoModels/order/createOrUpdateOrderActionRequest';

const OrderActionsEditor: FC = () => {
  const isSmallScreen = useResponsive('down', 'sm');
  const { orderId } = useParams();
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [editedAction, setEditedAction] = useState<OrderActionInfo>();
  const { data, isLoading } = useOrderActions(
    toNumber(orderId),
    isNumeric(orderId ?? '')
  );

  const actions = useMemo(() => sortBy(data, [(d) => d.action]), [data]);

  const createAction = (): OrderActionInfo => ({
    id: -Date.now(),
    action: actionsWhiteList[0] ?? OrderActionEnum.Request,
    dateCreate: new Date(),
    order: {
      id: +(orderId ?? 0),
      number: '',
      dateCreate: new Date(),
      state: OrderStateEnum.Request,
      address: '',
      client: { id: 0, fullName: '', phone: '' },
    },
    current: false,
  });

  const handleAddAction = (): void => {
    setEditedAction(createAction());
    setActionDialogOpen(true);
  };

  const handleActionDialogCancel: VoidFunction = () => {
    setActionDialogOpen(false);
    setEditedAction(undefined);
  };

  const handleActionDialogSave = async (action: OrderActionInfo): Promise<boolean> => {
    const isNew = !isNil(editedAction) && editedAction.id <= 0;

    const getRequest = (item: OrderActionInfo): CreateOrUpdateOrderActionRequest => ({
      action: item.action,
      current: item.current,
      dateAction: item.dateAction,
      userId: item.user?.id,
    });

    const promises = [
      isNew
        ? actionsService.createOrderActionAsync(+(orderId ?? 0), getRequest(action))
        : actionsService.updateOrderActionAsync(
            +(orderId ?? 0),
            action.id,
            getRequest(action)
          ),
    ];

    if (action.current && actions.some((a) => a.current)) {
      const currentActions = actions.filter((a) => a.current);
      promises.push(
        ...cloneDeep(currentActions).map(async (a) => {
          if (a.current) a.current = false;
          return await actionsService.updateOrderActionAsync(
            +(orderId ?? 0),
            a.id,
            getRequest(a)
          );
        })
      );
    }

    try {
      await Promise.allSettled(promises);

      await mutate(
        (key) => isArray(key) && key[0] === SWR_ORDER_ACTIONS_ENDPOINT,
        undefined
      );

      sendActionEditInfoToMetrika(action, isNew);

      setActionDialogOpen(false);
      setEditedAction(undefined);

      return true;
    } catch {
      return false;
    }
  };

  const handleEdit = (item: OrderActionInfo): void => {
    setEditedAction(item);
    setActionDialogOpen(true);
  };

  const sendActionEditInfoToMetrika = (action: OrderActionInfo, isNew: boolean): void => {
    if (process.env.NODE_ENV !== 'production' || !process.env.REACT_APP_METRIKA_ACCOUNT) {
      return;
    }

    ym(
      +process.env.REACT_APP_METRIKA_ACCOUNT,
      'reachGoal',
      'f1a6d2c9-28c2-45b6-b0a8-4073c9e267fe',
      {
        isNew,
        orderState: action.order.state,
        actionType: action.action,
        orderStateName: OrderStateEnum[action.order.state],
        actionTypeName: OrderActionEnum[action.action],
        isCurrent: action.current,
        hasDate: !!action.dateAction,
        hasUser: !!action.user,
      }
    );
  };

  const columns: Array<GridColDef<OrderActionInfo>> = [
    {
      field: 'current',
      headerName: 'Тек.',
      width: 60,
      renderCell: (params) =>
        params.row.current && <CheckCircleOutlineOutlinedIcon color='action' />,
    },
    {
      field: 'action',
      headerName: 'Этап',
      flex: 0.3,
      minWidth: 100,
      valueFormatter: (value) => getOrderActionName(value),
    },
    {
      field: 'dateAction',
      headerName: 'Дата',
      flex: 0.5,
      valueFormatter: (value) =>
        !isNil(value) ? dateFormatter.format(value) : 'Не задана',
    },
    {
      field: 'user',
      headerName: 'Ответственный',
      flex: 1,
      valueGetter: (_, row) => (!isNil(row.user) ? row.user.fullName : 'Не задан'),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 40,
      getActions: (params) => [
        <GridActionsCellItem
          key={params.row.id}
          icon={<EditIcon />}
          onClick={() => {
            handleEdit(params.row);
          }}
          label='Edit'
        />,
      ],
    },
  ];

  const getListSecondaryText = (item: OrderActionInfo): string => {
    const hasDateAction = !isNil(item.dateAction);
    const hasUser = !isNil(item.user);

    return (
      (hasDateAction ? dateFormatter.format(item.dateAction) : '') +
      (hasDateAction && hasUser ? ', ' : '') +
      (hasUser ? item.user?.fullName ?? '' : '')
    );
  };

  const actionsWhiteList = useMemo(() => {
    const defined = uniq(actions.map((a) => a.action)).filter(
      (a) =>
        a !== OrderActionEnum.Measurement &&
        a !== OrderActionEnum.Monolith &&
        a !== OrderActionEnum.Installation &&
        (!editedAction || a !== editedAction.action)
    );
    return enum2NumberArray(OrderActionEnum).filter((a) => !defined.includes(a));
  }, [actions, editedAction]);

  const getActionListTypographyProps = ({
    current,
  }: OrderActionInfo): {
    primaryTypographyProps: TypographyProps<'span', { component?: 'span' | undefined }>;
    secondaryTypographyProps: TypographyProps<'span', { component?: 'span' | undefined }>;
  } => ({
    primaryTypographyProps: current
      ? { color: 'secondary.main', fontWeight: 'bold' }
      : {},
    secondaryTypographyProps: current ? { color: 'secondary.light' } : {},
  });

  if (isLoading) {
    return <Skeleton variant='rounded' height={200} />;
  }

  return (
    <>
      <Stack spacing={2}>
        <Box>
          <Button variant='outlined' onClick={handleAddAction}>
            Добавить
          </Button>
        </Box>
        {isSmallScreen ? (
          <List disablePadding>
            {actions.map((item) => (
              <ListItem
                disableGutters
                key={item.id}
                secondaryAction={
                  <IconButton
                    edge='end'
                    aria-label='edit'
                    onClick={() => {
                      handleEdit(item);
                    }}>
                    <EditIcon />
                  </IconButton>
                }>
                <ListItemText
                  {...getActionListTypographyProps(item)}
                  primary={getOrderActionName(item.action)}
                  secondary={getListSecondaryText(item)}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <DataGrid
            columns={columns}
            rows={actions}
            autoHeight
            hideFooterPagination
            hideFooter
            disableRowSelectionOnClick
          />
        )}
      </Stack>

      {editedAction && (
        <ActionEditDialog
          open={actionDialogOpen}
          action={editedAction}
          whiteList={actionsWhiteList}
          onCancel={handleActionDialogCancel}
          onSave={handleActionDialogSave}
        />
      )}
    </>
  );
};

export default OrderActionsEditor;
